import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getCookie, QaToolKitOptions } from '@cashier/cashier-common';
import {
  Directions,
  FormStatuses,
  IntegrationThemes,
  PaymentTypes,
} from '@cashier/cashier-model';
import { useUIConfiguration } from '@cashier/cashier-shared';
import { cn, Loader } from '@cashier/cashier-ui';

import { AmountValueProvider, FieldRegistryProvider } from '../contexts';
import {
  useCashierConfiguration,
  useFormAction,
  useFormData,
  useFormPayloadItem,
  useFormStatus,
  usePaymentMethod,
  useSubmittingByEvent,
} from '../hooks';
import { AccountBalance } from '../modules/accountBalance';
import { AccountSelect } from '../modules/accountSelect/v2';
import { AgreementCheckbox } from '../modules/agreementCheckbox';
import { AmountField } from '../modules/amountField/v3';
import { AmountSelector } from '../modules/amountSelector/v2';
import { SecuredCardData, StandardCardData } from '../modules/cardData';
import { Description } from '../modules/description';
import { ErrorList } from '../modules/errorList/v2';
import { FieldWrapper } from '../modules/fieldWrapper/v2';

import styles from './styles.module.scss';

export const PaymentForm = (): ReactElement => {
  const status = useFormStatus();

  const { schema, version: formVersion } = useFormData();
  const { direction, paymentType } = useCashierConfiguration();

  useSubmittingByEvent();
  useFormAction();

  const { t, ready: translationsReady } = useTranslation();

  const paymentMethod = usePaymentMethod();

  const errorsPosition = useUIConfiguration('errors', 'position');
  const isBalanceVisible = useUIConfiguration('balance', 'visible');

  const isHostedFieldEnabled =
    getCookie(QaToolKitOptions.enableHostedField) === 'true';

  const { cardData, staticData, commonFieldMap } = schema || {};
  const { errors: amountErrors } = useFormPayloadItem(
    staticData?.amount?.name ?? '',
  );

  return (
    <div>
      <div className={cn([styles.section])}>
        {status === FormStatuses.INITIATED && (
          <div className={cn([styles.sectionContent, styles.loader])}>
            <div className={styles.loaderContainer}>
              <Loader size="medium" theme="colored" />
            </div>
            <div className={styles.loaderText}>{t('Loading')}</div>
          </div>
        )}

        {status !== FormStatuses.INITIATED && (
          <FieldRegistryProvider>
            <AmountValueProvider
              key={`amountValueProvider_${formVersion}`}
              initialValue={staticData?.amount ? staticData.amount.value : null}
            >
              {staticData?.account && (
                <AccountSelect
                  key={`accountSelect_${formVersion}`}
                  field={staticData.account}
                  version={'v3'}
                />
              )}

              {errorsPosition === 'top' && (
                <div className={styles.errorTopMargin}>
                  <ErrorList />
                </div>
              )}

              <>
                {isBalanceVisible &&
                  paymentType === PaymentTypes.Withdrawal && <AccountBalance />}
                <div
                  className={cn({
                    [styles.sectionContent]: true,
                    [styles.withoutTopMargin]:
                      paymentType === PaymentTypes.Withdrawal,
                  })}
                >
                  <div
                    className={cn({
                      [styles.form]: true,
                      [styles.empty]: !schema,
                      [styles.withoutMargin]: true,
                      [styles.bottomMargin]: true,
                    })}
                  >
                    <div
                      className={cn({
                        [styles.formField]: true,
                        [styles.emptyBottom]: !schema,
                      })}
                    >
                      <div
                        className={cn({
                          [styles.row]: true,
                          [styles.emptyTop]: !schema,
                          [styles.withoutMargin]: true,
                        })}
                      >
                        <div
                          className={cn({
                            [styles.col]: true,
                            [styles.full]: true,
                            [styles.withdrawal]:
                              paymentType === PaymentTypes.Withdrawal,
                            [styles.withdrawalContainer]:
                              paymentType === PaymentTypes.Withdrawal &&
                              isBalanceVisible,
                          })}
                        >
                          {staticData?.amount && (
                            <div className={styles.amountContainer}>
                              <AmountField
                                key={`amountField_${formVersion}`}
                                field={staticData.amount}
                              />
                              {staticData.amountSelector && (
                                <div className={styles.selectorWrapper}>
                                  <AmountSelector
                                    field={staticData.amountSelector}
                                    direction={direction}
                                    version="v3"
                                  />
                                </div>
                              )}
                              {amountErrors.length > 0 && (
                                <div
                                  className={cn({
                                    [styles.inlineError]: true,
                                    [styles.rtl]: direction === Directions.Rtl,
                                  })}
                                  data-test="amount-inline-error"
                                >
                                  {amountErrors.map((error, index) => (
                                    <div key={`${index}_${error}`}>{error}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/*{errorsPosition === 'bottom' && (*/}
                      {/*  <div className={styles.errorBottomMargin}>*/}
                      {/*    <ErrorList />*/}
                      {/*  </div>*/}
                      {/*)}*/}

                      {paymentMethod.description && (
                        <Description content={paymentMethod.description} />
                      )}

                      {commonFieldMap &&
                        Object.entries(commonFieldMap).map(([_, field]) => (
                          <FieldWrapper
                            key={`field_${field.name}_${formVersion}`}
                            field={field}
                            version={'v3'}
                          />
                        ))}

                      {cardData &&
                        (isHostedFieldEnabled ? (
                          <SecuredCardData
                            key={`cardData_${formVersion}`}
                            schema={cardData}
                          />
                        ) : (
                          <StandardCardData
                            key={`cardData_${formVersion}`}
                            schema={cardData}
                            version={'v3'}
                          />
                        ))}
                    </div>
                  </div>
                </div>
              </>

              {cardData?.termsAgreementToggler && translationsReady && (
                <AgreementCheckbox
                  key={`agreementCheckbox_${formVersion}`}
                  field={cardData.termsAgreementToggler}
                  theme={IntegrationThemes.Platform}
                  version={'v3'}
                />
              )}
            </AmountValueProvider>
          </FieldRegistryProvider>
        )}
      </div>
    </div>
  );
};
